import { Button, ButtonGroup } from "@blueprintjs/core";
import { useMemo } from "react";

const CELL_COUNT = 7;
const CELL_MID_LEN = ~~(CELL_COUNT / 2);

type Page = {
  ellipsis?: boolean;
  active?: boolean;
  nr: number;
};

export type PaginationProps = {
  totalItems: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  unlimited?: boolean;
};

export function Pagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  unlimited = false,
}: PaginationProps) {
  const totalPages = useMemo<number>(
    () => Math.ceil(totalItems / pageSize),
    [pageSize, totalItems],
  );
  const ltEnable = useMemo<boolean>(() => page > 1, [page]);
  const rtEnable = useMemo<boolean>(
    () => page < totalPages,
    [page, totalPages],
  );
  const pages = useMemo<Page[]>(() => {
    if (unlimited) {
      const ret: Page[] = [
        {
          nr: page,
          ellipsis: false,
          active: true,
        },
      ];

      if (page > 1) {
        if (page > 2) {
          ret.unshift({ nr: 1, ellipsis: true, active: false });
        }

        ret.unshift({ nr: 1, ellipsis: false, active: false });
      }

      return ret;
    }
    return getPagingLayout(totalPages, page);
  }, [page, totalPages, unlimited]);

  return (
    <ButtonGroup minimal>
      <Button
        icon="chevron-left"
        disabled={!ltEnable}
        onClick={() => onPageChange(page - 1)}
      />
      {pages.map((p) => (
        <Button
          key={p.nr}
          text={p.ellipsis ? "..." : p.nr}
          disabled={p.ellipsis}
          intent={p.active ? "primary" : "none"}
          active={p.active}
          onClick={() => onPageChange(p.nr)}
        />
      ))}
      <Button
        icon="chevron-right"
        disabled={!rtEnable}
        onClick={() => onPageChange(page + 1)}
      />
    </ButtonGroup>
  );
}

function getPagingLayout(totalCount: number, current: number): Page[] {
  let pages: Page[] = [];

  if (totalCount > CELL_COUNT) {
    pages[0] = { nr: 1 };
    pages[1] = { nr: 2 };
    pages[CELL_COUNT - 2] = { nr: totalCount - 1 };
    pages[CELL_COUNT - 1] = { nr: totalCount };

    if (current <= CELL_MID_LEN) {
      pages[CELL_COUNT - 2].ellipsis = true;
      for (let i = 2; i < CELL_COUNT - 2; i++) {
        pages[i] = { nr: i + 1 };
      }
    } else if (totalCount - current < CELL_MID_LEN) {
      pages[1].ellipsis = true;
      for (let i = 2; i < CELL_COUNT - 2; i++) {
        pages[i] = { nr: totalCount - CELL_COUNT + i + 1 };
      }
    } else {
      pages[1].ellipsis = true;
      pages[CELL_COUNT - 2].ellipsis = true;
      pages[CELL_MID_LEN] = { nr: current };

      for (let i = 1; i < CELL_COUNT - 5; i++) {
        pages[CELL_MID_LEN + i] = { nr: current + i };
        pages[CELL_MID_LEN - i] = { nr: current - i };
      }
    }
  } else {
    for (let i = 0; i < totalCount; i++) {
      pages[i] = { nr: i + 1, ellipsis: false };
    }
  }

  pages.forEach((p) => {
    if (p.nr === current) {
      p.active = true;
    }
  });

  return pages;
}
